import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useMemo } from 'react'
import { setBalanceEth } from '../../modules/web3/actions'
import { selectAccount, selectBalanceEth } from '../../modules/web3/selectors'
import { formatNumberToFirstDecimalDigit } from '../../page_components/Memecoins/TokenPage/helpers'
import useWeb3 from './index'
import { improvedAmountFromWei } from '../../contexts/StakingContext'
import { amountFromWei } from '../../utils/amount'

/**
 * Hook to get the ETH balance of the connected wallet.
 *
 * @returns An object containing the following properties:
 * - `ethBalanceInWei`: The ETH balance of the connected wallet
 * - `ethBalanceInEth`: The ETH balance of the connected wallet in ETH
 * - `ethBalanceInEthFormatted`: The ETH balance of the connected wallet in ETH formatted to the first non-zero decimal digit
 * - `getEthBalance`: A function to get the current ETH balance of the connected wallet. In case you need to get the balance for some action, and it needs to be the most recent.
 * - `updateEthBalance`: A function to update the ETH balance in the store.
 *
 */
const useEthBalance = () => {
  const { provider } = useWeb3()

  const ethBalanceInWei = useSelector(selectBalanceEth)
  const account = useSelector(selectAccount)
  const dispatch = useDispatch()

  const getEthBalance = useCallback(async () => {
    try {
      const balance = await provider.getBalance(account)
      return balance
    } catch (error) {
      console.error('Error on getEthBalance', error)
      return 0n
    }
  }, [provider, account])

  const updateEthBalance = useCallback(async () => {
    const balance = await getEthBalance()
    dispatch(setBalanceEth(String(balance)))
  }, [getEthBalance, dispatch])

  const ethBalanceInEth = amountFromWei(ethBalanceInWei).toString()
  const ethBalanceInEthFormatted =
    formatNumberToFirstDecimalDigit(ethBalanceInEth)

  return useMemo(
    () => ({
      ethBalanceInWei,
      ethBalanceInEth,
      ethBalanceInEthFormatted,
      getEthBalance,
      updateEthBalance,
    }),
    [
      ethBalanceInEth,
      ethBalanceInEthFormatted,
      ethBalanceInWei,
      getEthBalance,
      updateEthBalance,
    ],
  )
}

export default useEthBalance
